import { FormControlLabel, FormGroup, Grid } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Switch from "@material-ui/core/Switch";
import * as _ from "lodash";
import * as React from "react";
import { Subscription } from "rxjs";
import LoadObject from "../../load_objects/LoadObject";
import { ERRORS } from "../Errors";
import { LiveDataAnalyzer } from "../LiveDataAnalyzer";
import { FixtureDetailed, MultiKill, OneVersusX, PlayerExpanded, TeamStat, verified } from "../model/FixtureDetailed";
import { MatchData } from "../stores/FixtureDetailedStore";
import "./FixtureDetailedView.css";
import { FixtureMapTabsView } from "./FixtureMapTabsView";



export interface FixtureDetailedViewProps {
  id: number;
  fixtureLoader: LoadObject<Object>;
  legacy: boolean;
  isAdmin: boolean;
}

export interface State {
  fixtureSub: Subscription | undefined | null;
  fixture: FixtureDetailed | undefined;
  verification: VERIFICATION_MESSAGES | undefined;
  error: Error | undefined;
  historicFixture: FixtureDetailed | undefined;
  postmatchFixture: FixtureDetailed | undefined;
  isLiveFixture: boolean;
}


export enum VERIFICATION_MESSAGES {
  VERIFIED = "All stats verified",
  LIVE_DATA = "Stats computed from live data",
  NOT_LIVE_NOT_VERIFIED = "Stats have not been verified"
}

export class FixtureDetailedView extends React.Component<FixtureDetailedViewProps, State> {
  constructor(props: any) {
    super(props);
    this.state = this.getInitialState();
  }

  private getInitialState(): State {
    return {
      fixtureSub: undefined, fixture: undefined, error: undefined, verification: undefined, historicFixture: undefined, postmatchFixture: undefined, isLiveFixture: false};
  }

  public componentDidMount() {
    if (!this.state.fixtureSub) {
      _.defer(() => this.analyze());
    }
  }

  public componentDidUpdate(prevProps, prevState): void {
    this.componentDidMount();
  }

  public componentWillUnmount() {
    this.state.fixtureSub?.unsubscribe();
  }

  private async analyze() {
    const matchData: MatchData | undefined = ((this.props.fixtureLoader?.getValue() as any)?.matchDataMap as Map<number, MatchData>)?.get(this.props.id);
    const isVerified: boolean = (!!matchData?.fixture && matchData.fixture.status === "Ended" && verified(matchData.fixture));
    if (matchData && matchData.fixture && matchData.liveData === null) { // No live data
      if(!isVerified && new Date().getTime() - matchData.fixture.endTime <= 30000) { // 30 seconds
        this.setState({fixture: null!, error: new Error(ERRORS.VERIFICATION_BACKOFF)});
      } else {
        this.setState({fixture: matchData.fixture, error: new Error(ERRORS.FIXTURE_NO_LIVE_DATA),  fixtureSub: null, verification: isVerified ? VERIFICATION_MESSAGES.VERIFIED : VERIFICATION_MESSAGES.NOT_LIVE_NOT_VERIFIED })
      }      
      return;
    }

    if (!(matchData && matchData.fixture && matchData.liveData && !this.state.fixtureSub)) {
      return;
    }
  

    const analyzer = new LiveDataAnalyzer(matchData.fixture, matchData.liveData, this.props.legacy); // Live data present
    const fixtureSub = analyzer.getFixture().subscribe(
      (analyzedFixture: FixtureDetailed) => {
        const matchData: MatchData | undefined = ((this.props.fixtureLoader?.getValue() as any)?.matchDataMap as Map<number, MatchData>)?.get(this.props.id);
        if(isVerified) {
          const verificationMsg = isVerified ? VERIFICATION_MESSAGES.VERIFIED : VERIFICATION_MESSAGES.LIVE_DATA;
          try {
            const joinedFixture: FixtureDetailed = this.joinFixtures( matchData!.fixture!, analyzedFixture, this.props.isAdmin);      
            // this.setState({ fixture: joinedFixture, verification: verificationMsg, historicFixture: analyzedFixture, postmatchFixture: matchData!.fixture!})
            this.setState({ fixture: joinedFixture, verification: verificationMsg, historicFixture: analyzedFixture, postmatchFixture: joinedFixture});
          } catch (error) {
            this.setState({ fixture: matchData!.fixture!, error: error, verification: verificationMsg});
          }
        } else {
          this.setState({ fixture: analyzedFixture, verification: VERIFICATION_MESSAGES.LIVE_DATA });
        }
      },
      (error: Error) => {
        this.setState({fixture: matchData!.fixture!, error: error, verification: isVerified ? VERIFICATION_MESSAGES.VERIFIED : VERIFICATION_MESSAGES.NOT_LIVE_NOT_VERIFIED });       
        this.state.fixtureSub?.unsubscribe();
      },
      () => { }
    );
    this.setState({ fixtureSub: fixtureSub })
  }
  
  private joinFixtures(fixture: FixtureDetailed, analyzedFixture: FixtureDetailed, debug: boolean) : FixtureDetailed {
    const joinedFixture = _.cloneDeep(fixture);
    joinedFixture.maps.forEach((map, i) => {
      // Hack for missing round scores
      map.roundScores = map.roundScores.length === 2 ?  map.roundScores : analyzedFixture.maps[i]?.roundScores;
      map.roundScores = map.roundScores || [];
      
      const version: string = this.props.legacy ? "1.0" : "2.0";
      const prefix: string = `(${version}) ${fixture.id}/M${i + 1}: `;

      map.teamStats.forEach((teamStat) => {
        // 1st attempt:
        const analyzedTeamStat: TeamStat | undefined = analyzedFixture.maps[i].teamStats.filter(analyzedTeamStat => analyzedTeamStat.teamId === teamStat.teamId)[0];
        if (!analyzedTeamStat) {
          console.info(`${prefix}Could not find team ${JSON.stringify(_.pick(teamStat, ["teamId"]))} in the analyzed data`);
        } else {
          teamStat.players.forEach((player) => {
            let analyzedPlayer: PlayerExpanded | undefined;
            analyzedPlayer = analyzedTeamStat?.players.filter(analyzedPlayer => analyzedPlayer.playerId === player.playerId)[0]; // SEARCH BY ID
            if (!analyzedPlayer) {
              
              const playersMatchingByName: PlayerExpanded[] = analyzedTeamStat?.players.filter(analyzedPlayer => analyzedPlayer.name.toLowerCase().includes(player.name.toLowerCase())); //SEARCH BY NAME
              if (playersMatchingByName.length === 1) {
                debug && console.info(`${prefix}Found ${JSON.stringify(_.pick(player, ["playerId", "name"]))} by name`)
                analyzedPlayer = playersMatchingByName[0];
              } else {
                debug && console.info(`${prefix}Could not find ${JSON.stringify(_.pick(player, ["playerId", "name"]))} on ${JSON.stringify(_.pick(teamStat, ["teamId"]))} (Neither by ID or name)`);
              }
            }


            // 2nd attempt: Widening search to both teams
            if (!analyzedPlayer) {
              const matchedTeam = analyzedFixture.maps[i].teamStats.find(t => t.players.find(p => p.playerId === player.playerId || (p.name.toLowerCase().includes(player.name.toLowerCase()) || player.name.toLowerCase().includes(p.name.toLowerCase()))))!
              analyzedPlayer = matchedTeam?.players?.filter(analyzedPlayer => analyzedPlayer.playerId === player.playerId)[0]; // Search BOTH teams by ID
              if (matchedTeam && !analyzedPlayer) {
                const playersMatchingByName: PlayerExpanded[] = matchedTeam.players.filter(analyzedPlayer => (analyzedPlayer.name.toLowerCase().includes(player.name.toLowerCase())||player.name.toLowerCase().includes(analyzedPlayer.name.toLowerCase())));
                if (playersMatchingByName.length === 1) {
                  debug && console.info(`${prefix}Found ${JSON.stringify(_.pick(player, ["playerId", "name"]))} by name (on opposite team)`)
                  analyzedPlayer = playersMatchingByName[0];
                }
              }
              debug && analyzedPlayer && console.info(`${prefix}Found ${JSON.stringify(_.pick(player, ["playerId", "name"]))} on ${JSON.stringify(_.pick(matchedTeam, ["teamId"]))} (Wrong team?)`);
            }

            player.clutches = player.clutches?.map(clutch => {return {...clutch, mapNumber: i+1}});
            if (player && analyzedPlayer) {
              //player.oneVersusXs = analyzedPlayer.oneVersusXs;
              //player.multiKills = analyzedPlayer.multiKills;
              //player.entryKillClutches = analyzedPlayer.entryKillClutches;
            }
            else {
              debug && console.info(`${prefix} DISABLING CLUTCHES: Could not find {id: ${player.playerId}, name: ${player.name}} on either team`)
            }
          });
        }
      });
    });

    debug && fixture.maps.forEach((map, i) => {
      const version: string = this.props.legacy ? "1.0" : "2.0";
      const prefix: string = `(${version}) ${fixture.id}/M${i + 1}: `;

      const analyzedPlayers = analyzedFixture.maps[i].teamStats.flatMap(x => x.players);
      map.teamStats.flatMap(t => t.players).forEach(fixturePlayer => {
        const player: PlayerExpanded | undefined = analyzedPlayers.find(analyzedPlayer => analyzedPlayer.playerId === fixturePlayer.playerId || (fixturePlayer.name.toLowerCase().includes(analyzedPlayer.name.toLowerCase())||analyzedPlayer.name.toLowerCase().includes(fixturePlayer.name.toLowerCase())));
        if(player) {
          this.printDiscprepencies(fixturePlayer, player, prefix);
        }
      });
    });
    return joinedFixture;
  }


  private printDiscprepencies(player: PlayerExpanded, analyzedPlayer: PlayerExpanded, prefix?: string): void {
    if (player.kills !== analyzedPlayer.kills
      || player.deaths !== analyzedPlayer.deaths
      || player.assists !== analyzedPlayer.assists
      || player.flash_assists !== analyzedPlayer.flash_assists
      || player.entryKills !== analyzedPlayer.entryKills
    ) {
      // throw new Error(ERRORS.JOIN_ERROR_STATS);

      // console.debug(ERRORS.JOIN_ERROR_STATS)
      let discrepancies: string = `${prefix}` + player.name + " ";

      if (player.kills !== analyzedPlayer.kills) {
        discrepancies = discrepancies + (analyzedPlayer.kills - player.kills) + "K "
      }

      if (player.deaths !== analyzedPlayer.deaths) {
        discrepancies = discrepancies + (analyzedPlayer.deaths - player.deaths) + "D "

      }

      if (player.assists !== analyzedPlayer.assists) {
        discrepancies = discrepancies + (analyzedPlayer.assists - player.assists) + "A "

      }

      if (player.flash_assists !== analyzedPlayer.flash_assists) {
        discrepancies = discrepancies + (analyzedPlayer.flash_assists - player.flash_assists) + "A(f) "

      }

      if (player.entryKills !== analyzedPlayer.entryKills) {
        discrepancies = discrepancies + (analyzedPlayer.entryKills - player.entryKills) + "EK"

      }

      console.info(discrepancies)
    }

  }

  private copyClutches(): void {
    let oneVersusXs: OneVersusX[] =this.state.fixture!.maps
      .flatMap(map => map.teamStats)
      .flatMap(team => team.players)
      .flatMap(player => player.clutches?.flatMap(clutch => {return {mapNumber: clutch.mapNumber, roundNumber: clutch.round, id: Number(clutch.type.split("v")[1]), playerName: player.name} as OneVersusX}))
      .filter(clutch => clutch !== undefined);

    if(oneVersusXs.length == 0) {
        oneVersusXs = this.state.fixture!.maps
        .flatMap(map => map.teamStats)
        .flatMap(team => team.players)
        .flatMap(player => player.oneVersusXs!.flatMap(clutch => {return {...clutch, playerName: player.name} as OneVersusX}));
      }

      let multikills: MultiKill[] = this.state.fixture!.maps
      .flatMap(map => map.teamStats)
      .flatMap(team => team.players)
      .flatMap(player => player.multiKills?.flatMap(clutch => {return {...clutch, playerName: player.name} as MultiKill}))
      .filter(clutch => clutch !== undefined)
      .map(clutch => clutch!);


      oneVersusXs = oneVersusXs.filter(clutch => clutch!.id > 1);
      oneVersusXs.forEach(clutch => {
        (clutch as any).toString = () => `${(clutch as any).playerName},1v${clutch?.id},${clutch?.mapNumber},${clutch?.roundNumber}`;
      })

      multikills = multikills.filter(clutch => clutch!.id > 3);
      multikills.forEach(clutch => {
        (clutch as any).toString = () => `${(clutch as any).playerName},${clutch?.id}K,${clutch?.mapNumber},${clutch?.roundNumber}`;
      })

      let clutches = [... oneVersusXs, ...multikills];

      clutches = clutches.sort((a, b) => a.mapNumber - b.mapNumber);
      clutches = clutches.sort((a, b) => a.roundNumber - b.roundNumber);
    navigator.clipboard.writeText("player,stat,map,round\n" + clutches?.join("\n")!);
  }


  private onHistoricToggle(checked: boolean) {
    if(checked) {
      this.setState({...this.state, fixture: this.state.historicFixture, isLiveFixture: true});
    } else {
      this.setState({...this.state, fixture: this.state.postmatchFixture, isLiveFixture: false});
    }
  }
 
  
 render() {
    const matchData: MatchData | undefined = (this.props.fixtureLoader?.getValue() as any)?.matchDataMap?.get(this.props.id);
    let message : string | undefined;

    if(this.state.error?.message == ERRORS.VERIFICATION_BACKOFF) {
      message = this.state.error.message;
    }

    if (!matchData || matchData?.fixture === undefined || matchData?.liveData === undefined || this.state.fixture === undefined) {
      message = "Loading details...";
    } else if(matchData.fixture === null) {
      message = ERRORS.FIXTURE_NO_DATA;

    } else {
      // STARTED
      if(matchData.fixture.status === "Started") {
        if(matchData.liveData === null) {
          message = ERRORS.FIXTURE_NO_LIVE_DATA;
        } else if (matchData.liveData && !(this.state.fixture && this.state.fixture.maps.length > 0 && this.state.fixture.maps[0].teamStats.length > 0)) {        
          message = "Waiting for the match to start...";
        }
      }
  
      // ENDED
      if(matchData.fixture.status === "Ended") {
        if(!matchData.fixture.maps) {
          if(!this.props.legacy && matchData.liveData && !this.state.error) {
            message = ERRORS.FIXTURE_NO_MAP_FALLBACK;    
          } else {
            message = ERRORS.FIXTURE_NO_MAP;
          }
        }
      }
    }

    const scoreboardReady : boolean = !!this.state.fixture! && !!this.state.fixture?.maps && this.state.fixture?.maps[0]?.teamStats?.length > 0;
    return(
      <div style={{ display: "flex", flexDirection: "column" }}>
      {this.state.fixture && scoreboardReady ? <FixtureMapTabsView fixture={this.state.fixture} isLiveFixture={this.state.isLiveFixture} /> : undefined}
      {message && !scoreboardReady ? <div>{message}</div> : undefined}
      {scoreboardReady && <div className="scoreboard-controls">
          <div>
            {(this.state.verification == VERIFICATION_MESSAGES.VERIFIED || !this.state.error) && <Button size="small" variant="outlined" onClick={this.copyClutches.bind(this)}>Copy clutches to clipboard</Button>}
          </div>
          {this.state.historicFixture && this.state.postmatchFixture &&
          <Grid component="label" container alignItems="center" justify='center'>
              <Grid item>Verified Data</Grid>
              <Grid item><Switch color="default" onChange={(evt) => this.onHistoricToggle(evt.target.checked)} /></Grid>
              <Grid item>Live Data</Grid>
          </Grid>
          }
         {this.state.verification && <span>{this.state.verification}</span>}
        </div>}
    </div>
    );
  }
}
